import { type FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNewContractMutation } from '@api/queries/contract';
import { addToDate, calculateDateDifference } from '@common/lib/date';
import { SendAnalitics } from '@common/lib/playdeck';
import { milisecondsToTime } from '@common/lib/timer';
import { ModalContext } from '@components/Modals/Modal/config';
import { StringSmall } from '@components/Typography/Typography';
import { PurpleSmButton } from '@components/ui/Button/Button';
import { Picture } from '@components/ui/Picture/Picture';
import { Timer } from '@components/ui/Timer/Timer';
import { addToBalance } from '@redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { useGetContracts } from '@/common/hooks/request-for-get-contracts';

import css from './styles.module.scss';

import coinPng from '/img/tgem.png';
import coinWebp from '/img/tgem.webp';

interface ContractTimerProps {
  id: string;
  rejectedAt: string;
  contractType: string;
}

const ContractTimer: FC<ContractTimerProps> = ({
  rejectedAt,
  id,
  contractType,
}) => {
  const coinBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const getContracts = useGetContracts();
  const rejectLifetime = useAppSelector(
    (state) => state.user.user?.settings.contractRejectionLifetime,
  );
  const [time, setTime] = useState('');
  const [unlockCost, setUnlockCost] = useState<number | null>(null);
  const [getNewContract, { isLoading }] = useNewContractMutation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const ref = useRef<NodeJS.Timeout | undefined>();
  const { openModal } = useContext(ModalContext);

  useEffect(() => {
    const updateTime = () => {
      const endTime = addToDate(rejectedAt, (rejectLifetime || 4) * 60 * 1000);

      return (): void => {
        const dateNow = new Date().getTime();
        const difference = calculateDateDifference(endTime, dateNow);

        if (difference > 0) {
          const fortyFiveMinutes = 45 * 60 * 1000 + 999;
          const thirtyMinutes = 30 * 60 * 1000 + 999;
          const fifteenMinutes = 15 * 60 * 1000 + 999;
          const { seconds, minutes } = milisecondsToTime(difference);
          setUnlockCost(() =>
            difference > fortyFiveMinutes
              ? 4
              : difference > thirtyMinutes
                ? 3
                : difference > fifteenMinutes
                  ? 2
                  : 1,
          );
          setTime(`${minutes}:${seconds}`);
        } else {
          setTime('00:00');
          getContracts();
          clearInterval(ref.current);
        }
      };
    };

    updateTime()();
    ref.current = setInterval(updateTime(), 1000);

    return (): void => clearInterval(ref.current);
  }, [rejectedAt]);

  const onClick = (): void => {
    const cost = unlockCost || 0;

    if ((coinBalance || 0) >= cost) {
      getNewContract({ contractId: id }).then(() => {
        dispatch(addToBalance({ tgem: cost * -1 }));
        SendAnalitics.coinSpend({
          amount: cost,
          type: 'buy_next_contract',
          item_name: contractType,
        });
      });
    } else {
      openModal({
        renderKind: 'coinDeposit',
        props: { source: 'contract_timer' },
      });
    }
  };

  return (
    <div className={css.timer}>
      <div className="flex flex-col items-center w-full">
        <StringSmall
          weight="bold"
          className="text-white/40 max-w-[75px] text-center mb-1"
        >
          {t('New contract in')}
        </StringSmall>
        <Timer value={time} />
      </div>
      <div>
        <StringSmall
          weight="bold"
          className="text-white/40 max-w-[75px] text-center w-full mb-1"
        >
          {t('or get it now')}
        </StringSmall>
        <PurpleSmButton
          isLoading={isLoading}
          disabled={isLoading}
          className="flex items-center gap-1 !rounded-md"
          onClick={onClick}
        >
          <Picture
            className="w-4 h-4 shrink-0"
            img={coinPng}
            webp={coinWebp}
            alt="coin"
          />
          <StringSmall weight="bold">{unlockCost}</StringSmall>
        </PurpleSmButton>
      </div>
    </div>
  );
};

export { ContractTimer };
